import React from 'react';
import {
  Alert, Button, EmptyState, ErrorState, FilterChip, Table,
} from '@parsina/framework';
import { ImageOff, PackageSearch } from 'lucide-react';

import { useTranslation } from '../../i18n';
import { LISTINGS_QUERY, REVIEW_LISTING } from './queries';
import {
  ActionConfirm, Code, Fact, LoadMore, MarketDrawer, MoneyCell, NotInList, StatusChip, TableSkeleton,
  allIrr, useEnumLabel, usePagedList,
} from './shared';

export const LISTING_STATUSES = ['pending', 'approved', 'rejected'];

/** A pending listing gets both decisions; a decided one can be reversed. */
export const LISTING_ACTIONS = {
  pending: ['approve', 'reject'],
  approved: ['reject'],
  rejected: ['approve'],
};

const pickListings = (d) => d?.marketplaceListingReviews;

function Thumb({ image, size = 40 }) {
  const { t } = useTranslation();
  if (image?.url) {
    return <img src={image.url} alt={image.alt || ''} className="rounded-md border border-border object-cover" style={{ width: size, height: size }} />;
  }
  return (
    <span className="inline-flex items-center justify-center rounded-md border border-border bg-muted text-muted-foreground" style={{ width: size, height: size }}>
      <ImageOff size={16} aria-label={t('marketplace.listings.drawer.noImages')} />
    </span>
  );
}

/**
 * View «بررسی اقلام» — the review queue. `?status=` (default pending),
 * `?listing=<id>` opens the drawer with the gallery and the decision.
 */
export default function ListingsView({ view, go }) {
  const { t, formatDate } = useTranslation();
  const kindLabel = useEnumLabel('marketplace.listings.kind');
  const status = view.status || 'pending';
  const list = usePagedList(LISTINGS_QUERY, pickListings, { status });
  const selected = view.listing ? list.rows.find((r) => r.id === view.listing) : null;
  // The queue spans stores, and stores may price in different currencies: an
  // all-IRR page keeps «قیمت (تومان)», any other names no unit in the header
  // and puts one on every price.
  const unitPerFigure = !allIrr(list.rows);

  const columns = [
    { key: 'thumb', header: <span className="sr-only">{t('marketplace.listings.columns.thumb')}</span>, width: '4rem', render: (r) => <Thumb image={r.images?.[0]} /> },
    { key: 'title', header: t('marketplace.listings.columns.title'), render: (r) => <span dir="auto" className="font-semibold text-foreground">{r.title}</span> },
    {
      key: 'vendor',
      header: t('marketplace.listings.columns.vendor'),
      width: '12rem',
      render: (r) => (r.vendorName ? <span dir="auto">{r.vendorName}</span> : <span className="text-muted-foreground">{t('marketplace.listings.unknownVendor')}</span>),
    },
    { key: 'kind', header: t('marketplace.listings.columns.kind'), width: '6rem', render: (r) => kindLabel(r.kind) },
    {
      key: 'price',
      header: unitPerFigure ? t('marketplace.listings.columnsNoUnit.price') : t('marketplace.listings.columns.price'),
      align: 'end',
      width: '9rem',
      render: (r) => <MoneyCell rials={r.priceRial} currency={r.currency} withUnit={unitPerFigure} />,
    },
    { key: 'submitted', header: t('marketplace.listings.columns.submitted'), width: '9rem', render: (r) => formatDate(r.updatedAt) },
  ];

  return (
    <div data-testid="mk-listings">
      <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card p-3">
        <div role="group" aria-label={t('marketplace.listings.filters.label')} className="flex flex-wrap gap-1.5" data-testid="mk-listings-filter">
          {LISTING_STATUSES.map((s) => (
            <FilterChip
              key={s}
              label={t(`marketplace.listings.filters.${s}`)}
              active={status === s}
              onClick={() => go({ status: s, listing: '' })}
              data-testid={`mk-listings-filter-${s}`}
            />
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card transition-opacity" style={{ opacity: list.refreshing ? 0.45 : 1 }} aria-busy={list.refreshing || undefined}>
        {list.loading ? (
          <TableSkeleton cols={6} label={t('marketplace.listings.loading')} data-testid="mk-listings-loading" />
        ) : list.error ? (
          <ErrorState
            title={t('marketplace.listings.error')}
            description={list.error}
            onRetry={list.retry}
            retryLabel={t('common.actions.retry')}
            data-testid="mk-listings-error"
          />
        ) : (
          <>
            <Table
              dense
              ariaLabel={t('marketplace.listings.tableAria')}
              columns={columns}
              rows={list.rows}
              rowKey={(r) => r.id}
              onRowActivate={(r) => go({ listing: r.id })}
              rowProps={(r) => ({ 'data-testid': `mk-listings-row-${r.id}`, 'aria-label': t('marketplace.listings.open', { title: r.title }) })}
              empty={(
                <EmptyState
                  icon={<PackageSearch size={44} strokeWidth={1.5} aria-hidden />}
                  title={t('marketplace.listings.empty.title')}
                  description={t('marketplace.listings.empty.desc')}
                  data-testid="mk-listings-empty"
                />
              )}
              data-testid="mk-listings-table"
            />
            {list.cursor ? <LoadMore onClick={list.more} pending={list.moreLoading} data-testid="mk-listings-more" /> : null}
          </>
        )}
      </div>

      {view.listing ? (
        <ListingDrawer
          listing={selected}
          onClose={() => go({ listing: '' })}
          onChanged={(next) => { list.replace((r) => r.id === next.id, next); list.reload(); }}
        />
      ) : null}
    </div>
  );
}

function ListingDrawer({ listing, onClose, onChanged }) {
  const { t, formatDate } = useTranslation();
  const kindLabel = useEnumLabel('marketplace.listings.kind');
  const [action, setAction] = React.useState(null);
  const [error, setError] = React.useState(null);

  if (!listing) {
    return (
      <MarketDrawer onClose={onClose} title={t('marketplace.listings.drawer.title')} data-testid="mk-listing-drawer">
        <NotInList data-testid="mk-listing-drawer-missing" />
      </MarketDrawer>
    );
  }

  const actions = LISTING_ACTIONS[listing.reviewStatus] || [];
  const open = (decision) => {
    setError(null);
    setAction({
      key: decision,
      title: t(`marketplace.listings.confirm.${decision}`, { title: listing.title }),
      body: t(`marketplace.listings.confirm.${decision}Body`),
      confirmLabel: t(`marketplace.listings.actions.${decision}`),
      destructive: decision === 'reject',
      field: decision === 'reject' ? 'rejectNote' : 'note',
      document: REVIEW_LISTING,
      variables: (note) => ({ clinicId: listing.clinicId, id: listing.id, decision, note }),
      success: t(`marketplace.listings.success.${decision}`),
    });
  };

  return (
    <MarketDrawer
      onClose={onClose}
      title={t('marketplace.listings.drawer.title')}
      sub={listing.vendorSlug ? <Code>{listing.vendorSlug}</Code> : undefined}
      data-testid="mk-listing-drawer"
    >
      <div className="flex flex-col gap-5">
        {error ? <Alert variant="error" data-testid="mk-listing-drawer-error">{t('marketplace.drawer.failed', { message: error })}</Alert> : null}
        {listing.sourceInactive ? <Alert variant="warning">{t('marketplace.listings.drawer.sourceInactive')}</Alert> : null}

        <div>
          <div dir="auto" className="text-base font-bold text-foreground">{listing.title}</div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <span dir="auto">{listing.vendorName || t('marketplace.listings.unknownVendor')}</span>
            <StatusChip value={listing.reviewStatus} prefix="marketplace.listings.review" data-testid="mk-listing-drawer-status" />
          </div>
        </div>

        <section>
          <h2 className="mb-2 text-sm font-bold text-foreground">{t('marketplace.drawer.actions')}</h2>
          {actions.length ? (
            <div className="flex flex-wrap gap-2">
              {actions.map((decision) => (
                <Button
                  key={decision}
                  variant={decision === 'approve' ? 'default' : 'destructive'}
                  onClick={() => open(decision)}
                  data-testid={`mk-listing-${decision}`}
                >
                  {t(`marketplace.listings.actions.${decision}`)}
                </Button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{t('marketplace.drawer.noActions')}</p>
          )}
        </section>

        <section data-testid="mk-listing-drawer-gallery">
          <h2 className="mb-2 text-sm font-bold text-foreground">{t('marketplace.listings.drawer.gallery')}</h2>
          {listing.images?.length ? (
            <ul className="grid grid-cols-3 gap-2">
              {listing.images.map((img, i) => (
                <li key={img.id}>
                  {img.url ? (
                    <img
                      src={img.url}
                      alt={img.alt || t('marketplace.listings.image', { n: i + 1 })}
                      className="aspect-square w-full rounded-lg border border-border object-cover"
                    />
                  ) : (
                    <div className="flex aspect-square w-full items-center justify-center rounded-lg border border-border bg-muted text-muted-foreground">
                      <ImageOff size={20} aria-label={t('marketplace.listings.image', { n: i + 1 })} />
                    </div>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">{t('marketplace.listings.drawer.noImages')}</p>
          )}
        </section>

        <dl className="grid grid-cols-2 gap-4 text-sm">
          <Fact label={t('marketplace.listings.columns.kind')} value={kindLabel(listing.kind)} />
          <Fact label={t('marketplace.listings.drawer.productType')} value={listing.productType ? <Code>{listing.productType}</Code> : null} />
          <Fact label={t('marketplace.listings.drawer.price')} value={<MoneyCell rials={listing.priceRial} currency={listing.currency} />} />
          <Fact label={t('marketplace.listings.drawer.listPrice')} value={<MoneyCell rials={listing.listPriceRial} currency={listing.currency} />} />
          <Fact label={t('marketplace.listings.columns.submitted')} value={listing.updatedAt ? formatDate(listing.updatedAt) : null} />
          {listing.reviewNote ? <Fact wide label={t('marketplace.listings.drawer.reviewNote')} value={<span dir="auto">{listing.reviewNote}</span>} /> : null}
          <Fact
            wide
            label={t('marketplace.listings.drawer.description')}
            value={listing.description
              ? <span dir="auto" className="whitespace-pre-line font-normal">{listing.description}</span>
              : <span className="font-normal text-muted-foreground">{t('marketplace.listings.drawer.noDescription')}</span>}
          />
        </dl>
      </div>

      <ActionConfirm
        action={action}
        testId="mk-listing-dialog"
        onClose={() => setAction(null)}
        onError={setError}
        onDone={(d) => { setError(null); if (d?.reviewMarketplaceListing) onChanged(d.reviewMarketplaceListing); }}
      />
    </MarketDrawer>
  );
}
